// The condition builder (spec/dashboard.md section 7.1): the closed box in words, the open tree with bracket groups and
// inline leaf sentences built from the typed widgets, the grouped [+] picker; every action stages through ops.ts.
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Json, Parameter } from '../../api/types';
import { useGame } from '../../shell/GameContext';
import { Button, IconButton } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { ChevronUp, Eye, Pencil, Plus, X } from '../../ui/icons';
import { Checkbox, Select, TextInput } from '../../ui/inputs';
import { Popover } from '../../ui/Popover';
import { formatDuration, useTimeZone } from '../../ui/time';
import { useWorkspaceVersion } from '../../workspace/react';
import type { DocumentLike } from '../format';
import { FieldWidget } from '../widgets/FieldWidget';
import { useEditorHost, useWidgetEnv, type WidgetProps } from '../widgets/context';
import { COMPARISONS, CONDITION_KINDS, CONDITION_NS, DAYS, daysText, isConditionTemplate, kindOf, minutesOf, type ConditionKind } from './model';
import { useConfirm } from '../../ui/ConfirmDialog';
import { addNode, childIds, collectTree, createNode, deleteNode, editNode, nodeOf, replaceNode, setSlot, toggleLogic, type Slot, type TreeEnv } from './ops';
import { PlayerPreview, PreviewVerdictBadge } from './PlayerPreview';
import { elementFields, profileFields } from './profile';
import { conditionText, timeStatus, timeStatusText, type TimeStatus } from './text';

/** The context filter of the builder: the condition templates a page does not offer (section 8.3: no ABTestCondition inside an override group). */
export const ConditionOptionsContext = createContext<{ exclude: readonly string[] }>({ exclude: [] });

/**
 * Section 7.1 Clear / Remove this group: dropping a node with children asks first (the subtree is counted); a leaf and
 * an empty group go at once. The staging itself is deleteNode's, unchanged (orphan-free: the subtree is removed too).
 */
function useDropNode(env: TreeEnv): (slot: Slot, id: string) => Promise<void> {
  const confirm = useConfirm();
  return async (slot, id) => {
    if (childIds(nodeOf(env.catalog, id)).length > 0) {
      const n = collectTree(env.catalog, id).ids.length;
      const ok = await confirm({ title: 'Clear the condition?', body: `${n} node${n === 1 ? ' is' : 's are'} removed; undo brings them back until you save.`, confirmLabel: 'Clear', tone: 'danger' });
      if (!ok) return;
    }
    deleteNode(env, slot, id);
  };
}

/** The owner-side widget for a `document → SmartObjects.Conditions.Logic` parameter: the closed box, Edit, the tree. */
export function ConditionField({ param: p, value, onCommit, disabled, compact, owner, id }: WidgetProps) {
  const widgetEnv = useWidgetEnv();
  const options = useContext(ConditionOptionsContext);
  const env = useMemo<TreeEnv>(() => ({ workspace: widgetEnv.workspace, catalog: widgetEnv.catalog, exclude: options.exclude }), [widgetEnv.workspace, widgetEnv.catalog, options.exclude]);
  const { catalog, workspace } = env;
  useWorkspaceVersion(workspace);
  const host = useEditorHost();
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  const [open, setOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const drop = useDropNode(env);
  const rootId = typeof value === 'string' ? value : null;
  const text = conditionText(catalog, rootId, { zone, offsets });
  const status = timeStatus(catalog, rootId, Math.floor(Date.now() / 1000), { zone, offsets });
  if (compact) {
    return (
      <span className="row" title={text}>
        <span className="condition-text">{text}</span>
        <IconButton label={`Edit ${p.displayName ?? p.name} in the document`} onClick={() => owner && host?.open(owner)} disabled={!owner || !host}>
          <Pencil aria-hidden="true" />
        </IconButton>
      </span>
    );
  }
  const slot: Slot = { kind: 'owner', set: (next) => onCommit(next) };
  return (
    <div className="condition-field" id={id} data-testid={`condition-${p.name}`}>
      <div className="row condition-head">
        <span className="condition-text">{text}</span>
        <TimeStatusBadge status={status} />
        <span className="spacer" />
        {!disabled && (
          <Button size="s" onClick={() => setOpen((o) => !o)} aria-expanded={open} icon={open ? <ChevronUp aria-hidden="true" /> : rootId ? <Pencil aria-hidden="true" /> : <Plus aria-hidden="true" />}>
            {open ? 'Collapse' : rootId ? 'Edit' : 'Add condition'}
          </Button>
        )}
        {!disabled && open && (
          <Button size="s" variant="ghost" onClick={() => setPreviewing((v) => !v)} aria-pressed={previewing} icon={<Eye aria-hidden="true" />}>
            Preview as player
          </Button>
        )}
        {!disabled && rootId && (
          <Button size="s" variant="ghost" onClick={() => void drop(slot, rootId)} icon={<X aria-hidden="true" />}>
            Clear
          </Button>
        )}
      </div>
      {open && !disabled && (previewing ? (
        <PlayerPreview rootId={rootId}>
          <ConditionTree env={env} rootId={rootId} slot={slot} />
        </PlayerPreview>
      ) : (
        <ConditionTree env={env} rootId={rootId} slot={slot} />
      ))}
    </div>
  );
}

export function TimeStatusBadge({ status }: { status: TimeStatus }) {
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  const tone = status.kind === 'in' ? 'success' : status.kind === 'scheduled' ? 'info' : status.kind === 'outside' ? 'warning' : 'neutral';
  return (
    <Badge tone={tone} title={status.failing.length ? `Not passing now: ${status.failing.join('; ')}` : undefined}>
      {timeStatusText(status, zone, offsets)}
    </Badge>
  );
}

/** The open builder over a root slot. */
export function ConditionTree({ env, rootId, slot }: { env: TreeEnv; rootId: string | null; slot: Slot }) {
  if (!rootId) {
    return (
      <div className="condition-tree">
        <AddNodeButton env={env} slot={slot} root label="Add the first condition" />
      </div>
    );
  }
  return (
    <div className="condition-tree">
      <NodeView env={env} id={rootId} slot={slot} depth={0} />
    </div>
  );
}

function NodeView({ env, id, slot, depth, elementTemplate }: { env: TreeEnv; id: string; slot: Slot; depth: number; elementTemplate?: string | null }) {
  const { catalog } = env;
  const drop = useDropNode(env);
  const node = nodeOf(catalog, id);
  if (!node) {
    return (
      <div className="node-row">
        <span className="loading-line" role="status">
          <span className="spinner" aria-hidden="true" />
          Loading <code>{id.slice(0, 8)}</code>
        </span>
      </div>
    );
  }
  const key = node.template.startsWith(CONDITION_NS) ? node.template.slice(CONDITION_NS.length) : node.template;
  const data = (node.data ?? {}) as Record<string, Json>;
  if (key === 'And' || key === 'Or') {
    const kids = ((data.conditions as Json[] | undefined) ?? []).filter((c): c is string => typeof c === 'string');
    return (
      <div className={`logic-group ${key.toLowerCase()}`} data-testid={`logic-${id}`}>
        <div className="row logic-head">
          <button type="button" className={`logic-badge ${key.toLowerCase()}`} onClick={() => toggleLogic(env, slot, id)} title="Toggle and / or">
            {key === 'And' ? 'and' : 'or'}
          </button>
          <span className="muted small">{kids.length === 0 ? (key === 'And' ? 'empty: always passes' : 'empty: never passes') : `${kids.length} condition${kids.length === 1 ? '' : 's'}`}</span>
          <span className="spacer" />
          <IconButton label="Remove this group" onClick={() => void drop(slot, id)}>
            <X aria-hidden="true" />
          </IconButton>
        </div>
        <div className="logic-children">
          {kids.map((c, i) => (
            <NodeView key={`${c}:${i}`} env={env} id={c} slot={{ kind: 'child', parentId: id, field: 'conditions', index: i }} depth={depth + 1} elementTemplate={elementTemplate} />
          ))}
          <AddNodeButton env={env} slot={{ kind: 'child', parentId: id, field: 'conditions' }} elementTemplate={elementTemplate} />
        </div>
      </div>
    );
  }
  if (key === 'Not') {
    const child = typeof data.condition === 'string' ? data.condition : null;
    return (
      <div className="logic-group not" data-testid={`logic-${id}`}>
        <div className="row logic-head">
          <span className="logic-badge not">not</span>
          <span className="spacer" />
          <IconButton label="Remove this group" onClick={() => void drop(slot, id)}>
            <X aria-hidden="true" />
          </IconButton>
        </div>
        <div className="logic-children">{child ? <NodeView env={env} id={child} slot={{ kind: 'child', parentId: id, field: 'condition' }} depth={depth + 1} elementTemplate={elementTemplate} /> : <AddNodeButton env={env} slot={{ kind: 'child', parentId: id, field: 'condition' }} elementTemplate={elementTemplate} />}</div>
      </div>
    );
  }
  return (
    <div className="node-row" data-testid={`leaf-${id}`}>
      <LeafEditor env={env} node={node} slot={slot} elementTemplate={elementTemplate} />
      <span className="spacer" />
      <PreviewVerdictBadge id={id} />
      <IconButton label="Remove this condition" onClick={() => deleteNode(env, slot, id)}>
        <X aria-hidden="true" />
      </IconButton>
    </div>
  );
}

// ---- the leaf sentences -----------------------------------------------------------------------------------------------

function LeafEditor({ env, node, slot, elementTemplate }: { env: TreeEnv; node: DocumentLike; slot: Slot; elementTemplate?: string | null }) {
  const { catalog } = env;
  const key = node.template.startsWith(CONDITION_NS) ? node.template.slice(CONDITION_NS.length) : node.template;
  const data = (node.data ?? {}) as Record<string, Json>;
  const template = catalog.templates.get(node.template);
  const params = new Map((template?.effectiveParameters ?? []).map((p) => [p.name, p]));
  const field = (name: string, extra?: Partial<Parameter>) => {
    const p = params.get(name);
    if (!p) return <span className="error small">({name}?)</span>;
    return <FieldWidget param={{ ...p, ...extra }} value={data[name]} compact owner={node.id} onCommit={(v) => editNode(env, node.id, name, v)} />;
  };
  const cmp = (allowIn?: boolean, stringOps?: boolean) => (
    <Select size="s" value={String(data.comparison ?? 0)} aria-label="Comparison" onChange={(e) => {
      const v = e.target.value;
      if (v === 'in') {
        const n = Number(data.value ?? 0);
        replaceNode(env, slot, node.id, `${CONDITION_NS}ProfileFieldInRange`, { fullPath: data.fullPath ?? null, min: n, max: n + 1 });
        return;
      }
      editNode(env, node.id, 'comparison', Number(v));
    }}>
      {COMPARISONS.filter((c) => c.value !== 6 || stringOps).map((c) => (
        <option key={c.value} value={String(c.value)}>
          {c.label}
        </option>
      ))}
      {allowIn && <option value="in">∈ (in range)</option>}
    </Select>
  );
  const path = <span className="path-chip" title={JSON.stringify(data.fullPath)}>{conditionPath(data.fullPath)}</span>;
  switch (key) {
    case 'ProfileFieldNumber':
      return <Sentence>{path}{cmp(true)}{field('value')}</Sentence>;
    case 'ProfileFieldLong':
      return <Sentence>{path}{cmp()}{field('value')}</Sentence>;
    case 'ProfileFieldString':
      return <Sentence>{path}{cmp(false, true)}{Number(data.comparison) === 6 ? null : field('value')}</Sentence>;
    case 'ProfileFieldBool':
      return <Sentence>{path}<span>is</span>{field('value')}</Sentence>;
    case 'ProfileFieldInRange': {
      const bad = Number(data.max ?? 0) <= Number(data.min ?? 0);
      return (
        <Sentence>
          {path}
          <Select size="s" value="in" aria-label="Comparison" onChange={(e) => { if (e.target.value !== 'in') replaceNode(env, slot, node.id, `${CONDITION_NS}ProfileFieldNumber`, { fullPath: data.fullPath ?? null, comparison: Number(e.target.value), value: Number(data.min ?? 0) }); }}>
            {COMPARISONS.filter((c) => c.value !== 6).map((c) => (
              <option key={c.value} value={String(c.value)}>
                {c.label}
              </option>
            ))}
            <option value="in">∈ (in range)</option>
          </Select>
          <span>[</span>{field('min')}<span>,</span>{field('max')}<span>)</span>
          {bad && (
            <span className="error small" role="alert">
              an empty range never passes ([min, max) is half-open)
            </span>
          )}
        </Sentence>
      );
    }
    case 'ProfileFieldList': {
      const check = Number(data.checkType ?? 0);
      const element = (() => {
        const fp = data.fullPath as { profile?: string; path?: string } | null;
        if (!fp?.path) return elementTemplate ?? null;
        const fields = fp.profile && fp.profile !== 'SmartObjects.UnnyProfile' && catalog.templates.get(fp.profile)?.kind !== 'profile_root' ? elementFields(catalog.templates, fp.profile) : profileFields(catalog.templates);
        return fields.find((f) => f.path === fp.path && f.profile === fp.profile)?.elementTemplate ?? null;
      })();
      const filter = typeof data.filter === 'string' ? data.filter : null;
      const inner = typeof data.condition === 'string' ? data.condition : null;
      return (
        <div className="stack">
          <Sentence>
            {field('checkType')}<span>of</span>{path}
            {check === 0 && cmp()}
            {check === 0 && field('value')}
          </Sentence>
          <div className="sub-slot">
            <span className="muted small">where (filter):</span>
            {filter ? <NodeView env={env} id={filter} slot={{ kind: 'child', parentId: node.id, field: 'filter' }} depth={1} elementTemplate={element} /> : <AddNodeButton env={env} slot={{ kind: 'child', parentId: node.id, field: 'filter' }} elementTemplate={element} label="filter" />}
          </div>
          {check !== 0 && (
            <div className="sub-slot">
              <span className="muted small">matches:</span>
              {inner ? <NodeView env={env} id={inner} slot={{ kind: 'child', parentId: node.id, field: 'condition' }} depth={1} elementTemplate={element} /> : <AddNodeButton env={env} slot={{ kind: 'child', parentId: node.id, field: 'condition' }} elementTemplate={element} label="element condition" />}
            </div>
          )}
        </div>
      );
    }
    case 'TimeRage': {
      const start = Number(data.start ?? 0);
      const finish = Number(data.finish ?? 0);
      const repeat = Number(data.repeat ?? 0);
      const warn = repeat > 0 && repeat <= finish - start;
      return (
        <Sentence>
          <span>Dates [</span>{field('start')}<span>,</span>{field('finish')}<span>] every</span>
          <FieldWidget param={{ ...params.get('repeat')!, name: 'repeat', displayName: 'Repeat every' }} value={data.repeat} compact owner={node.id} onCommit={(v) => editNode(env, node.id, 'repeat', v)} />
          {finish <= start && <span className="error small">the range is empty</span>}
          {warn && (
            <span className="error small" role="alert">
              repeat ({formatDuration(repeat)}) must be longer than the range ({formatDuration(finish - start)})
            </span>
          )}
        </Sentence>
      );
    }
    case 'TimeOfTheDay': {
      const s = minutesOf(data.start);
      const f = minutesOf(data.finish);
      return (
        <Sentence>
          <span>Time</span>{field('start')}<span>–</span>{field('finish')}
          {s !== null && f !== null && f < s && <span className="muted small">(overnight: wraps past midnight)</span>}
          {(s === null || f === null) && <span className="error small">HH:MM</span>}
        </Sentence>
      );
    }
    case 'DayOfTheWeek': {
      const mask = Number(data.days ?? 0);
      return (
        <Sentence>
          <span>Days:</span>
          <span className="row wrap" role="group" aria-label="Days">
            {DAYS.map((d) => (
              <Checkbox key={d.bit} label={d.name.slice(0, 3)} checked={(mask & d.bit) === d.bit} onChange={(e) => editNode(env, node.id, 'days', e.target.checked ? mask | d.bit : mask & ~d.bit)} />
            ))}
          </span>
          <span className="muted small">= {daysText(mask)}</span>
        </Sentence>
      );
    }
    case 'InventoryItems':
      return <Sentence><span>inventory</span>{field('item')}{cmp()}{field('value')}</Sentence>;
    case 'StoreItemWasPurchased':
      return <Sentence><span>purchased</span>{field('storeItem')}{cmp()}{field('value')}</Sentence>;
    case 'ProductWasPurchased':
      return <Sentence><span>purchased product</span>{field('product')}</Sentence>;
    case 'Revenue':
      return <Sentence>{field('type')}{cmp()}{field('value')}<span>during the last</span>{field('days')}<span>days</span></Sentence>;
    case 'ActiveEvent':
      return <Sentence><span>event</span>{field('gameEvent')}<span>is active</span></Sentence>;
    case 'ActiveOffer':
      return <Sentence><span>offer</span>{field('gameOffer')}<span>is active</span></Sentence>;
    case 'SegmentCondition':
      return <Sentence><span>in segment</span>{field('segment')}</Sentence>;
    case 'ABTestCondition':
      return <Sentence><span>A/B test</span>{field('test')}<span>variant</span>{field('variant')}</Sentence>;
    case 'AppVersion':
      return <Sentence><span>app version</span>{cmp()}{field('value')}</Sentence>;
    case 'InstallVersion':
      return <Sentence><span>install version</span>{cmp()}{field('value')}</Sentence>;
    case 'EngineVersion':
      return <Sentence><span>engine version</span>{cmp()}{field('value')}</Sentence>;
    case 'Platform':
      return <Sentence><span>platform</span>{field('value')}</Sentence>;
    case 'BalancyPlatform':
      return <Sentence><span>store platform</span>{field('value')}</Sentence>;
    case 'Country':
      return <Sentence><span>country code</span>{field('code')}</Sentence>;
    case 'SystemLanguage':
      return <Sentence><span>language</span>{field('value')}</Sentence>;
    case 'Empty':
      return <Sentence><span>pass</span>{field('pass')}</Sentence>;
    default:
      return (
        <Sentence>
          <Badge>{template?.displayName ?? key}</Badge>
          {(template?.effectiveParameters ?? []).map((p) => (
            <span key={p.name} className="row">
              <span className="muted small">{p.displayName ?? p.name}</span>
              {field(p.name)}
            </span>
          ))}
        </Sentence>
      );
  }
}

function Sentence({ children }: { children: ReactNode }) {
  return <span className="sentence">{children}</span>;
}

function conditionPath(fullPath: Json | undefined): string {
  const fp = fullPath as { profile?: string; path?: string } | null | undefined;
  if (!fp?.path) return 'field?';
  const profile = fp.profile && fp.profile !== 'SmartObjects.UnnyProfile' ? `${fp.profile.split('.').pop()} › ` : '';
  return profile + fp.path.replace(/\./g, ' › ');
}

// ---- the [+] picker ---------------------------------------------------------------------------------------------------

interface PickEntry {
  key: string;
  group: string;
  label: string;
  hint?: string;
  run: () => void;
}

export function AddNodeButton({ env, slot, root, label = '', elementTemplate }: { env: TreeEnv; slot: Slot; root?: boolean; label?: string; elementTemplate?: string | null }) {
  const { catalog, workspace } = env;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [text, setText] = useState('');
  const entries = useMemo<PickEntry[]>(() => {
    const out: PickEntry[] = [];
    const place = (kind: ConditionKind | string, data: Record<string, Json> = {}) => {
      if (root && slot.kind === 'owner') {
        const key = typeof kind === 'string' ? kind : kind.template;
        if (key === `${CONDITION_NS}And` || key === `${CONDITION_NS}Or` || key === `${CONDITION_NS}Not`) {
          addNode(env, slot, kind, data);
          return;
        }
        // the root And appears with the first leaf
        const rootId = createNode(env, `${CONDITION_NS}And`);
        setSlot(env, slot, rootId);
        addNode(env, { kind: 'child', parentId: rootId, field: 'conditions' }, kind, data);
        return;
      }
      addNode(env, slot, kind, data);
    };
    const excluded = new Set(env.exclude ?? []);
    for (const kind of CONDITION_KINDS) {
      if (kind.profile) continue;
      if (!catalog.templates.has(kind.template) || excluded.has(kind.template)) continue;
      out.push({ key: kind.template, group: kind.group, label: kind.label, run: () => place(kind) });
    }
    const fields = elementTemplate ? elementFields(catalog.templates, elementTemplate) : profileFields(catalog.templates);
    for (const f of fields) {
      if (!catalog.templates.has(f.leaf)) continue;
      out.push({ key: `${f.profile}:${f.path}`, group: f.group, label: f.label, hint: f.type, run: () => place(f.leaf, { fullPath: { profile: f.profile, path: f.path } as unknown as Json }) });
    }
    for (const t of catalog.templateList) {
      if (!isConditionTemplate(t.name) || kindOf(t.name) || t.flags.includes('abstract')) continue;
      if (['Base', 'Logic', 'TimeBase', 'ProfileField', 'ProfileFieldCompare', 'DatesRage'].includes(t.name.slice(CONDITION_NS.length))) continue;
      out.push({ key: t.name, group: 'Custom', label: t.displayName ?? t.name, run: () => place(t.name) });
    }
    return out;
  }, [catalog, env, slot, root, elementTemplate]);
  const q = text.trim().toLowerCase();
  const shown = q ? entries.filter((e) => e.label.toLowerCase().includes(q) || e.group.toLowerCase().includes(q) || e.key.toLowerCase().includes(q)) : entries;
  const groups = Array.from(new Set(shown.map((e) => e.group)));
  const order = ['Inventory', 'LiveOps', 'Logic', 'System', 'Time', 'Custom'];
  groups.sort((a, b) => (order.indexOf(a) === -1 ? 100 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 100 : order.indexOf(b)) || a.localeCompare(b));
  const passLast = groups.filter((g) => g !== 'Pass').concat(groups.includes('Pass') ? ['Pass'] : []);
  void workspace;
  return (
    <>
      <Button size="s" variant="ghost" onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="dialog" aria-label={label === '' ? 'Add a condition' : label} icon={<Plus aria-hidden="true" />}>
        {label}
      </Button>
      <Popover anchor={anchor} open={anchor !== null} onClose={() => setAnchor(null)} label="Add a condition" className="condition-picker">
        <TextInput size="s" autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Search conditions and profile fields…" aria-label="Search conditions" />
        <div className="picker-groups">
          {passLast.map((g) => (
            <div key={g} className="picker-group">
              <div className="picker-group-title">{g}</div>
              {shown
                .filter((e) => e.group === g)
                .slice(0, 60)
                .map((e) => (
                  <button
                    key={e.key}
                    type="button"
                    className="picker-entry"
                    onClick={() => {
                      e.run();
                      setAnchor(null);
                      setText('');
                    }}
                  >
                    {e.label}
                    {e.hint && <span className="muted small"> · {e.hint}</span>}
                  </button>
                ))}
            </div>
          ))}
          {shown.length === 0 && <p className="muted small">No condition or profile field matches.</p>}
        </div>
      </Popover>
    </>
  );
}

/** For cards and grids: the tree in words plus the status badge. */
export function ConditionSummary({ rootId }: { rootId: string | null | undefined }) {
  const { catalog } = useWidgetEnv();
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  const text = conditionText(catalog, rootId ?? null, { zone, offsets });
  const status = timeStatus(catalog, rootId ?? null, Math.floor(Date.now() / 1000), { zone, offsets });
  return (
    <span className="row wrap">
      <span className="condition-text">{text}</span>
      <TimeStatusBadge status={status} />
    </span>
  );
}

export { childIds };
